import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useHistory, Link, useLocation } from "react-router-dom";
import { createUseStyles } from "react-jss";
import dateFormat from "dateformat";
import { Text } from "$components";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { Company, CompanyType, Warehouse } from "$types";
import useJStyles from "$hooks/useJStyles";
import { EntrepotService, CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import { theme } from "$utils/theme/bataillog";

interface InitialFilters {
  companyFilters?: {
    $inCompanyTypes: CompanyType[];
    companyTypes?: CompanyType;
  };
  warehouseFilters?: any; // optional, maybe we'll need it later
  includeRelatedFiles?: boolean;
}

const useStockList = (
  contributor?: Company,
  initialFilters: InitialFilters = {}
) => {
  const { jclasses } = useJStyles();
  const { search } = useLocation();
  const classes = useStyles();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any>({ thead: [], bodyData: [] });
  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const renderEntrepotData = useCallback(
    (warehouse: Warehouse, company: Company) => {
      const importedFile = warehouse.importedFiles
        ?.sort(
          (f1: any, f2: any) =>
            new Date(f2.date).getTime() - new Date(f1.date).getTime()
        )
        .find((importedFile: any) => importedFile.company === company._id);
      const date1 = new Date().setHours(0, 0, 0, 0);
      const date2 = new Date(importedFile?.date).setHours(0, 0, 0, 0);
      return (
        <span
          className={clsx(classes.entrepot, date1 > date2 && "outdate")}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: importedFile
              ? dateFormat(
                  importedFile.date,
                  `${DEFAULT_DATE_FORMAT}"<br/>"HH"h"MM`
                )
              : "<span>-</span>",
          }}
        />
      );
    },
    [classes.entrepot]
  );

  const renderCompany = useCallback(
    (c: any) => {
      return (
        <Link
          to={`/stock/${c._id}`}
          style={{ textDecoration: "none", maxWidth: "100%" }}
        >
          <Text className={jclasses.jEllipsedText} type="important">
            {c.name}
          </Text>
        </Link>
      );
    },
    [jclasses.jEllipsedText]
  );

  const fetchWarehouses = useCallback(async () => {
    const filters = {
      page: 1,
      rowsPerPage: 1000,
      ...(contributor?._id && { company: contributor?._id }),
      ...(initialFilters.includeRelatedFiles && {
        includeRelatedFiles: initialFilters.includeRelatedFiles,
      }),
    };
    const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
    let getWarehouses: any = await queryClient.getQueryData(queryKey);
    if (!getWarehouses) {
      getWarehouses = await queryClient.fetchQuery(
        queryKey,
        () => EntrepotService.getEntrepots(filters),
        { cacheTime: 5 * 60 * 1000 }
      );
      queryClient.setQueryData(queryKey, getWarehouses);
    }
    setWarehouses(getWarehouses?.data || []);
  }, [contributor?._id, initialFilters.includeRelatedFiles]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const fecthCompanies = useCallback(async () => {
    const filters = {
      isActive: true,
      page: 1,
      rowsPerPage: 1000,
      ...(contributor?._id && { companyTypes: CompanyType.CLIENT }),
      ...(initialFilters.companyFilters?.companyTypes && {
        companyTypes: initialFilters.companyFilters.companyTypes,
      }),
      ...(initialFilters.companyFilters?.$inCompanyTypes && {
        $inCompanyTypes: initialFilters.companyFilters.$inCompanyTypes,
      }),
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        () => CompanyServices.getCompanies(filters),
        { cacheTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies, contributor?._id, initialFilters.companyFilters]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  const filteredWarehouses = useMemo(() => {
    return warehouses?.filter((e) => {
      const isManageable = e.isManageable !== false; // warehouses without "isManageable must be displayed too
      if (contributor) {
        return e.parentCompany === contributor._id && isManageable;
      }
      return (
        (companies?.some((c) => c._id === e.parentCompany) ||
          e.companies.some((entrepotCompany) =>
            companies?.some((c) => c._id === entrepotCompany._id)
          )) &&
        isManageable
      );
    });
  }, [companies, contributor, warehouses]);

  // Update stock table
  useEffect(() => {
    const thead = [
      {
        id: "entreprise",
        label: (
          <div className={jclasses.jTopHead}>
            <div className={jclasses.jTopheadLabel}>Entreprise</div>
          </div>
        ),
      },
      ...(filteredWarehouses || []).map((e: any, index) => ({
        id: `entrepot${index}`,
        label: (
          <div className={jclasses.jTopHead}>
            <div className={jclasses.jTopheadLabel}>{e.name}</div>
          </div>
        ),
      })),
    ];
    const onClickAction = (company: Company) =>
      history.push(`/stock/${company._id}${search}`); // preserve url parameters such as contributor

    const bodyData = companies.map((c: any) => [
      {
        value: renderCompany(c),
        onClickAction: () => onClickAction(c),
      },
      ...(filteredWarehouses || []).map((e: any) => ({
        value: renderEntrepotData(e, c),
        onClickAction: () => onClickAction(c),
      })),
    ]);
    setTableData({ thead, bodyData });
  }, [
    companies,
    contributor,
    filteredWarehouses,
    history,
    jclasses.jTopHead,
    jclasses.jTopheadLabel,
    renderCompany,
    renderEntrepotData,
    search,
  ]);

  return {
    classes,
    filteredWarehouses,
    isLoading,
    setIsLoading,
    tableData,
  };
};

const useStyles = createUseStyles(() => ({
  entrepot: {
    color: theme.color.green,
    "&.outdate": {
      color: theme.color.red,
    },
    "& span": {
      color: theme.color.gray5,
    },
  },
  noResultPlaceHolder: {
    minHeight: "420px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

export default useStockList;
